import type { CartCategoryGroup, CartLine, CartSupplierGroup, GroupedCart } from './types'

const groupLines = (
  lines: CartLine[],
  section: CartSupplierGroup['section'],
  supplierKind: CartLine['supplierKind'],
  supplierKey: (line: CartLine) => string,
): CartSupplierGroup[] => {
  const bySupplier = new Map<string, CartLine[]>()

  for (const line of lines) {
    const key = supplierKey(line)
    const bucket = bySupplier.get(key) ?? []
    bucket.push(line)
    bySupplier.set(key, bucket)
  }

  return [...bySupplier.entries()]
    .sort(([left], [right]) => left.localeCompare(right, 'ru'))
    .map(([, supplierLines]) => {
      const byCategory = new Map<number, CartLine[]>()
      for (const line of supplierLines) {
        const bucket = byCategory.get(line.productTypeId) ?? []
        bucket.push(line)
        byCategory.set(line.productTypeId, bucket)
      }

      const categories: CartCategoryGroup[] = [...byCategory.entries()]
        .sort(([leftId], [rightId]) => leftId - rightId)
        .map(([productTypeId, categoryLines]) => ({
          productTypeId,
          productTypeName: categoryLines[0]?.productTypeName ?? `Категория #${productTypeId}`,
          lines: categoryLines.sort((left, right) => left.itemName.localeCompare(right.itemName, 'ru')),
        }))

      const first = supplierLines[0]
      return {
        section,
        supplierKind,
        supplierOrganizationId: first?.supplierOrganizationId ?? null,
        privateSupplierId: first?.privateSupplierId ?? null,
        supplierOrganizationName: first?.supplierOrganizationName ?? 'Поставщик',
        categories,
        lineCount: supplierLines.length,
      }
    })
}

export const groupCartLines = (lines: CartLine[]): GroupedCart => {
  const registered = lines.filter((line) => line.supplierKind === 'registered')
  const external = lines.filter((line) => line.supplierKind === 'external')

  const inProvisium = groupLines(
    registered,
    'in_provisium',
    'registered',
    (line) => String(line.supplierOrganizationId ?? 0),
  )
  const externalGroups = groupLines(
    external,
    'external',
    'external',
    (line) => String(line.privateSupplierId ?? line.supplierOrganizationName),
  )

  return {
    inProvisium,
    external: externalGroups,
    all: [...inProvisium, ...externalGroups],
  }
}
